/* AdminSupport — admin's central inbox for every support ticket from every
 * user (buyer + seller + admin). All tickets land here, including auto-
 * generated ones from the error-to-ticket bridge.
 *
 * Layout:
 *   • 5 KPI tiles (open / in progress / reply needed / resolved / total)
 *   • Filters: status, urgency, role, free-text search
 *   • Table: # / Subject / User / Role / Urgency / Category / Status / Updated
 *   • Click row → drawer with full message thread, reply box, status actions
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search, Loader2, ChevronRight, X, Send, Clock, AlertCircle,
  Check, Inbox, CheckCircle2, RefreshCw, MessageSquare, User2,
  Tag, Filter as FilterIcon,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";

const STATUS_OPTIONS = [
  { v: "",             l: "All" },
  { v: "open",         l: "Open" },
  { v: "in_progress",  l: "In progress" },
  { v: "waiting_user", l: "Reply needed" },
  { v: "resolved",     l: "Resolved" },
  { v: "closed",       l: "Closed" },
];

const URGENCY_OPTIONS = [
  { v: "",       l: "Any urgency" },
  { v: "urgent", l: "Urgent" },
  { v: "high",   l: "High" },
  { v: "normal", l: "Normal" },
  { v: "low",    l: "Low" },
];

const ROLE_OPTIONS = [
  { v: "",       l: "All roles" },
  { v: "buyer",  l: "Buyer" },
  { v: "seller", l: "Seller" },
  { v: "admin",  l: "Admin" },
];

// Curated known categories with pretty labels. Any *unknown* category coming
// back from the live `category_breakdown` is auto-merged into the dropdown
// (raw key shown) so we never silently hide a real ticket.
const KNOWN_CATEGORIES = [
  { v: "",                        l: "All categories" },
  { v: "general",                 l: "General" },
  { v: "billing",                 l: "Billing" },
  { v: "master_policy_reapply",   l: "Allianz reapply" },
  { v: "kyb",                     l: "KYB verification" },
  { v: "kyc",                     l: "KYC verification" },
  { v: "payouts",                 l: "Payouts" },
  { v: "wallet",                  l: "Wallet" },
  { v: "credit",                  l: "Credit / underwriting" },
  { v: "shipping",                l: "Shipping" },
  { v: "claims",                  l: "QI claims" },
  { v: "insurance",               l: "Insurance" },
  { v: "integrations",            l: "Integrations" },
  { v: "fraud",                   l: "Fraud / abuse" },
  { v: "abuse",                   l: "Abuse report" },
  { v: "feature_request",         l: "Feature request" },
  { v: "bug_report",              l: "Bug report" },
];

// Map raw category key → friendly label (used in table cell + pills).
const CATEGORY_LABEL = Object.fromEntries(
  KNOWN_CATEGORIES.filter((c) => c.v).map((c) => [c.v, c.l]),
);
const prettyCategory = (raw) => CATEGORY_LABEL[raw] || raw || "general";

const STATUS_BADGE = {
  open:          { label: "Open",         color: "bg-amber-50 text-amber-800 border-amber-200" },
  in_progress:   { label: "In progress",  color: "bg-blue-50 text-blue-700 border-blue-200" },
  waiting_user:  { label: "Reply needed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  resolved:      { label: "Resolved",     color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:        { label: "Closed",       color: "bg-secondary text-foreground/55 border-border" },
};

const URGENCY_PILL = {
  urgent: "bg-rose-50 text-rose-800 border-rose-200",
  high:   "bg-amber-50 text-amber-800 border-amber-200",
  normal: "bg-secondary text-foreground/70 border-border",
  low:    "bg-secondary/60 text-foreground/55 border-border",
};


export default function AdminSupport() {
  const [data, setData] = useState({ items: [], breakdown: {}, urgency_breakdown: {}, category_breakdown: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", urgency: "", role: "", category: "", q: "" });
  const [active, setActive] = useState(null); // ticket detail drawer

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.urgency) params.set("urgency", filters.urgency);
      if (filters.role) params.set("role", filters.role);
      if (filters.category) params.set("category", filters.category);
      if (filters.q.trim()) params.set("q", filters.q.trim());
      const r = await api.get(`/admin/support/tickets?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load tickets");
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { load(); }, []);

  // Re-query when filters change (debounce search input)
  useEffect(() => {
    const t = setTimeout(load, filters.q ? 350 : 0);
    return () => clearTimeout(t);
  }, [filters.status, filters.urgency, filters.role, filters.category, filters.q]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const kpis = useMemo(() => {
    const b = data.breakdown || {};
    const total = Object.values(b).reduce((a, c) => a + c, 0);
    return {
      open:        b.open || 0,
      in_progress: b.in_progress || 0,
      waiting:     b.waiting_user || 0,
      resolved:    b.resolved || 0,
      total,
    };
  }, [data.breakdown]);

  // Merge curated KNOWN_CATEGORIES with any extra categories the backend
  // reports — keeps the dropdown comprehensive even when new categories
  // ship (e.g. `master_policy_reapply` from the Allianz reapply flow).
  const categoryOptions = useMemo(() => {
    const known = new Set(KNOWN_CATEGORIES.map((c) => c.v));
    const extras = (data.category_breakdown || [])
      .map((c) => c.category)
      .filter((c) => c && !known.has(c))
      .map((c) => ({ v: c, l: prettyCategory(c) }));
    return [...KNOWN_CATEGORIES, ...extras];
  }, [data.category_breakdown]);

  return (
    <div data-testid="admin-support-page">
      <PageHeader
        title="Support inbox"
        subtitle="Every ticket from every user — auto-filed errors, manual tickets, replies. Single pane of glass."
        action={
          <Button variant="outline" onClick={load} data-testid="admin-support-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
          </Button>
        }
      />

      {/* KPI tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-support-kpis">
        <Kpi label="Open"         value={kpis.open}        color="text-amber-700"   icon={Inbox}/>
        <Kpi label="In progress"  value={kpis.in_progress} color="text-blue-700"    icon={Clock}/>
        <Kpi label="Reply needed" value={kpis.waiting}     color="text-emerald-700" icon={MessageSquare}/>
        <Kpi label="Resolved"     value={kpis.resolved}    color="text-foreground/60" icon={CheckCircle2}/>
        <Kpi label="Total"        value={kpis.total}       color="text-foreground"  icon={Tag}/>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="admin-support-filters">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-3.5 h-3.5 text-foreground/40 absolute top-1/2 -translate-y-1/2 left-3"/>
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search ticket #, subject, body, email…"
            className="pl-9 h-10"
            data-testid="admin-support-search"
          />
        </div>
        <FilterPills
          label="Status"
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={(v) => setFilters({ ...filters, status: v })}
          dataTestId="admin-support-status"
        />
        <select
          value={filters.urgency}
          onChange={(e) => setFilters({ ...filters, urgency: e.target.value })}
          data-testid="admin-support-urgency"
          className="h-10 rounded-lg border border-border bg-white px-3 text-sm"
        >
          {URGENCY_OPTIONS.map(o => (<option key={o.v} value={o.v}>{o.l}</option>))}
        </select>
        <select
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          data-testid="admin-support-role"
          className="h-10 rounded-lg border border-border bg-white px-3 text-sm"
        >
          {ROLE_OPTIONS.map(o => (<option key={o.v} value={o.v}>{o.l}</option>))}
        </select>
        <select
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          data-testid="admin-support-category"
          className="h-10 rounded-lg border border-border bg-white px-3 text-sm"
        >
          {categoryOptions.map(o => (<option key={o.v || "all"} value={o.v}>{o.l}</option>))}
        </select>
      </div>

      {/* Categories — quick stats */}
      {Array.isArray(data.category_breakdown) && data.category_breakdown.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="admin-support-categories">
          <span className="text-[10px] mono uppercase tracking-widest text-foreground/55 mr-1">
            <FilterIcon className="w-3 h-3 inline mr-1"/>Top categories:
          </span>
          {data.category_breakdown.slice(0, 8).map((c) => (
            <button
              key={c.category}
              onClick={() => setFilters({ ...filters, category: c.category })}
              data-testid={`admin-support-category-${c.category}`}
              className="text-[11px] px-2.5 py-1 rounded-full border border-border bg-white hover:bg-secondary/40"
              title={`${c.count} tickets`}
            >
              {prettyCategory(c.category)} <span className="text-foreground/45">· {c.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-foreground/55 text-sm inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-4 h-4 animate-spin"/>Loading tickets…
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center text-foreground/55 text-sm">
            <Inbox className="w-7 h-7 mx-auto text-foreground/30 mb-2"/>
            No tickets match these filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-[10px] mono uppercase tracking-widest text-foreground/55">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">#</th>
                <th className="text-left px-4 py-2.5 font-medium">Subject</th>
                <th className="text-left px-4 py-2.5 font-medium">User</th>
                <th className="text-left px-4 py-2.5 font-medium">Role</th>
                <th className="text-left px-4 py-2.5 font-medium">Urgency</th>
                <th className="text-left px-4 py-2.5 font-medium">Category</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="text-left px-4 py-2.5 font-medium">Updated</th>
                <th className="text-left px-4 py-2.5 font-medium"/>
              </tr>
            </thead>
            <tbody>
              {data.items.map((t) => {
                const sb = STATUS_BADGE[t.status] || STATUS_BADGE.open;
                const up = URGENCY_PILL[t.urgency] || URGENCY_PILL.normal;
                return (
                  <tr
                    key={t.id}
                    className="border-t border-border hover:bg-secondary/30 cursor-pointer"
                    onClick={() => setActive(t.id)}
                    data-testid={`admin-support-row-${t.number}`}
                  >
                    <td className="px-4 py-3 mono text-[11.5px] text-foreground/70 whitespace-nowrap">{t.number}</td>
                    <td className="px-4 py-3 max-w-[320px]">
                      <div className="font-medium truncate">{t.subject}</div>
                      <div className="text-[11px] text-foreground/55 truncate">
                        {t.messages?.length || 1} msg
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[12.5px] truncate">{t.user_name || "—"}</div>
                      <div className="text-[11px] text-foreground/55 truncate">{t.user_email || ""}</div>
                    </td>
                    <td className="px-4 py-3 capitalize text-[12.5px]">{t.user_role || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${up}`}>{t.urgency}</span>
                    </td>
                    <td className="px-4 py-3 text-[12.5px] text-foreground/70">{prettyCategory(t.category)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sb.color}`}>{sb.label}</span>
                    </td>
                    <td className="px-4 py-3 text-[11.5px] text-foreground/55 whitespace-nowrap">
                      {new Date(t.updated_at || t.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-foreground/30"/>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {active && (
        <TicketDrawer
          ticketId={active}
          onClose={() => setActive(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, color, icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-center justify-between text-[10px] mono uppercase tracking-widest text-foreground/55">
        <span>{label}</span>
        <Icon className="w-3.5 h-3.5"/>
      </div>
      <div className={`mt-1.5 text-2xl display ${color}`}>{value}</div>
    </div>
  );
}

function FilterPills({ label, options, value, onChange, dataTestId }) {
  return (
    <div className="inline-flex rounded-full border border-border bg-white p-0.5 text-xs" data-testid={dataTestId}>
      {options.map((o) => (
        <button
          key={o.v || "all"}
          onClick={() => onChange(o.v)}
          data-testid={`${dataTestId}-${o.v || "all"}`}
          className={`px-3 py-1.5 rounded-full transition-colors ${value === o.v ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

/* ─── Ticket detail drawer ───────────────────────────────────────────────── */
function TicketDrawer({ ticketId, onClose, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    api.get(`/support/tickets/${ticketId}`)
      .then((r) => setTicket(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load ticket"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setError(null);
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, { body: reply.trim() });
      setReply("");
      load();
      onChanged && onChanged();
      toast.success("Reply sent");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    setStatusBusy(true);
    try {
      await api.patch(`/support/tickets/${ticketId}/status`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    } finally {
      setStatusBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="admin-ticket-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <div className="w-full max-w-xl bg-white border-l border-border h-full flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare className="w-4 h-4 text-foreground/55 shrink-0"/>
            <div className="font-medium text-sm truncate">
              {ticket ? `#${ticket.number} — ${ticket.subject}` : "Loading…"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-ticket-drawer-close"
            className="text-foreground/45 hover:text-foreground"
          >
            <X className="w-4 h-4"/>
          </button>
        </div>

        {!ticket ? (
          <div className="flex-1 grid place-items-center text-foreground/55 text-sm">
            {error ? <span className="text-rose-700">{error}</span> : <Loader2 className="w-5 h-5 animate-spin"/>}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className={`px-2 py-0.5 rounded-full border ${(STATUS_BADGE[ticket.status] || STATUS_BADGE.open).color}`}>
                  {(STATUS_BADGE[ticket.status] || STATUS_BADGE.open).label}
                </span>
                <span className={`px-2 py-0.5 rounded-full border ${URGENCY_PILL[ticket.urgency] || URGENCY_PILL.normal}`}>
                  {ticket.urgency}
                </span>
                <span className="px-2 py-0.5 rounded-full border border-border bg-secondary text-foreground/65">
                  {prettyCategory(ticket.category)}
                </span>
              </div>
              <div className="rounded-xl border border-border bg-secondary/30 p-3 text-[12.5px]">
                <div className="flex items-center gap-2 text-foreground/65">
                  <User2 className="w-3.5 h-3.5"/>
                  <span className="font-medium">{ticket.user_name || ticket.user_email}</span>
                  <span className="capitalize text-foreground/45">· {ticket.user_role}</span>
                </div>
                <div className="text-[11px] text-foreground/55 mt-0.5">
                  {ticket.user_email} · opened {new Date(ticket.created_at).toLocaleString()}
                </div>
              </div>

              <ul className="space-y-2" data-testid="admin-ticket-messages">
                {(ticket.messages || []).map((m) => (
                  <li
                    key={m.id}
                    className={`rounded-xl p-3 text-[12.5px] ${m.author_role === "admin" ? "bg-foreground text-background" : "bg-secondary/60 border border-border"}`}
                  >
                    <div className={`text-[10px] mono uppercase tracking-widest mb-1 ${m.author_role === "admin" ? "text-accent/80" : "text-foreground/55"}`}>
                      {m.author_role === "admin" ? "verqo support (you)" : (m.author_name || "User")}
                      {" · "}
                      {new Date(m.at).toLocaleString()}
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-border p-4 space-y-2 shrink-0">
              <form onSubmit={sendReply} className="space-y-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply to user…"
                  rows={3}
                  data-testid="admin-ticket-reply"
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
                />
                {error && <div className="text-rose-700 text-[11px]">{error}</div>}
                <Button
                  type="submit"
                  disabled={sending || !reply.trim()}
                  data-testid="admin-ticket-reply-send"
                  className="w-full rounded-full"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Send className="w-3.5 h-3.5 mr-1.5"/>}
                  Send reply
                </Button>
              </form>
              <div className="flex flex-wrap gap-2 pt-1">
                {["in_progress", "waiting_user", "resolved", "closed"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    disabled={statusBusy || ticket.status === s}
                    data-testid={`admin-ticket-status-${s}`}
                    className={`text-[11px] px-3 py-1.5 rounded-full border transition-colors ${
                      ticket.status === s
                        ? "bg-foreground text-background border-foreground"
                        : "border-border hover:bg-secondary"
                    } disabled:opacity-50`}
                  >
                    Mark {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
